import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ListTodo } from 'lucide-react';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Banner } from '@/components/ui/Banner';
import { ColumnSortHeader } from '@/components/ui/ColumnSortHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FilterChip, StatusChip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { ResponsiveTable } from '@/components/ui/Table';
import { TaskFormSlideOver } from '@/components/tasks/TaskFormSlideOver';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import { useStaffTasks, useToggleStaffTaskDone } from '@/hooks/useStaffTasks';
import {
  STAFF_TASK_DEADLINE_FILTERS,
  STAFF_TASK_SCOPES,
  STAFF_TASK_STATUSES,
  staffTaskIsOverdue,
  staffTaskMatchesDeadlineFilter,
  type StaffTask,
  type StaffTaskDeadlineFilter,
  type StaffTaskScope,
  type StaffTaskStatus,
} from '@/lib/staffTasks';
import {
  DEFAULT_STAFF_TASK_LIST_SORT,
  nextStaffTaskListSort,
  sortStaffTasks,
  type StaffTaskListSort,
  type StaffTaskSortColumn,
} from '@/lib/staffTaskListSort';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

function statusTone(status: StaffTaskStatus) {
  switch (status) {
    case 'open':
      return 'upcoming' as const;
    case 'in_progress':
      return 'draft' as const;
    case 'done':
      return 'completed' as const;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function priorityTone(priority: StaffTask['priority']) {
  switch (priority) {
    case 'high':
      return 'coral' as const;
    case 'normal':
      return 'draft' as const;
    case 'low':
      return 'neutral' as const;
    default: {
      const _exhaustive: never = priority;
      return _exhaustive;
    }
  }
}

function formatDeadline(task: StaffTask, locale: typeof da): string {
  if (!task.deadline_date) return '—';
  const date = format(new Date(`${task.deadline_date}T00:00:00`), 'd. MMM yyyy', { locale });
  if (task.deadline_time) return `${date} ${task.deadline_time.slice(0, 5)}`;
  return date;
}

export function Tasks() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { user, hasPermission } = useAuth();
  const userId = user?.id ?? null;
  const canCreate = Boolean(activeClinicId && hasPermission(activeClinicId, 'tasks', 'create'));
  const { data: clinicUsers } = useClinicUsersList(activeClinicId);
  const query = useStaffTasks(activeClinicId);
  const toggleDone = useToggleStaffTaskDone();
  const dateLocale = i18n.language === 'da' ? da : enUS;

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<StaffTaskScope>('mine');
  const [assigneeId, setAssigneeId] = useState('');
  const [status, setStatus] = useState<StaffTaskStatus | 'all'>('all');
  const [deadline, setDeadline] = useState<StaffTaskDeadlineFilter | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StaffTask | null>(null);
  const [sort, setSort] = useState<StaffTaskListSort>(DEFAULT_STAFF_TASK_LIST_SORT);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (query.data ?? []).filter((task) => {
      if (scope === 'mine') {
        if (!userId || !task.assignees.some((assignee) => assignee.user_id === userId))
          return false;
        if (task.status === 'done') return false;
      } else if (scope === 'created_by_me') {
        if (!userId || task.created_by !== userId) return false;
      } else if (scope === 'all') {
        if (task.status === 'done') return false;
      } else if (scope === 'done') {
        if (task.status !== 'done') return false;
      }
      if (assigneeId && !task.assignees.some((assignee) => assignee.user_id === assigneeId)) {
        return false;
      }
      if (status !== 'all' && task.status !== status) return false;
      if (
        !staffTaskMatchesDeadlineFilter(
          task.deadline_date,
          task.deadline_time,
          task.status,
          deadline,
        )
      ) {
        return false;
      }
      if (needle) {
        const hay = [
          task.title,
          task.description ?? '',
          task.assignees.map((assignee) => assignee.name).join(' '),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [query.data, scope, userId, assigneeId, status, deadline, search]);

  const sorted = useMemo(() => sortStaffTasks(filtered, sort), [filtered, sort]);

  const counts = useMemo(() => {
    const list = query.data ?? [];
    return {
      mine: list.filter(
        (task) =>
          userId &&
          task.status !== 'done' &&
          task.assignees.some((assignee) => assignee.user_id === userId),
      ).length,
      created_by_me: list.filter((task) => userId && task.created_by === userId).length,
      all: list.filter((task) => task.status !== 'done').length,
      done: list.filter((task) => task.status === 'done').length,
    };
  }, [query.data, userId]);

  const sortHeader = (column: StaffTaskSortColumn, label: string) => {
    const active = sort.column === column;
    return (
      <ColumnSortHeader
        label={label}
        active={active}
        direction={sort.direction}
        onClick={() => setSort((current) => nextStaffTaskListSort(current, column))}
        ariaLabel={
          active
            ? t(sort.direction === 'asc' ? 'tasks.sorted_asc' : 'tasks.sorted_desc', {
                column: label,
              })
            : t('tasks.sort_by', { column: label })
        }
      />
    );
  };

  const sortableColumns: { id: StaffTaskSortColumn; label: string }[] = [
    { id: 'title', label: t('tasks.col_title') },
    { id: 'assignees', label: t('tasks.col_assignees') },
    { id: 'deadline', label: t('tasks.col_deadline') },
    { id: 'priority', label: t('tasks.field_priority') },
    { id: 'status', label: t('tasks.field_status') },
  ];

  return (
    <PageLayout>
      <div className="flex min-h-10 w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('tasks.title')}</h1>
        </div>
        <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('tasks.search_placeholder')}
            aria-label={t('tasks.search_placeholder')}
          />
        </div>
        <div className="flex flex-wrap items-center gap-s2 nav:shrink-0 nav:flex-nowrap">
          <Select
            aria-label={t('tasks.filter_assignee')}
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
          >
            <option value="">{t('tasks.filter_assignee_all')}</option>
            {(clinicUsers ?? []).map((member) => (
              <option key={member.user_id} value={member.user_id}>
                {member.user?.name?.trim() || member.user?.email || member.user_id}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t('tasks.field_status')}
            value={status}
            onChange={(event) => setStatus(event.target.value as StaffTaskStatus | 'all')}
          >
            <option value="all">{t('tasks.filter_status_all')}</option>
            {STAFF_TASK_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`tasks.status.${value}`)}
              </option>
            ))}
          </Select>
          {canCreate ? (
            <Button type="button" onClick={() => setCreating(true)} disabled={!activeClinicId}>
              {t('tasks.create')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-s4 flex flex-wrap items-center gap-s2">
        {STAFF_TASK_SCOPES.map((value) => (
          <FilterChip
            key={value}
            pressed={scope === value}
            count={counts[value]}
            onClick={() => setScope(value)}
          >
            {t(`tasks.scope.${value}`)}
          </FilterChip>
        ))}
        {STAFF_TASK_DEADLINE_FILTERS.map((value) => (
          <FilterChip
            key={value}
            pressed={deadline === value}
            onClick={() => setDeadline((current) => (current === value ? null : value))}
          >
            {t(`tasks.deadline_filter.${value}`)}
          </FilterChip>
        ))}
      </div>

      <div className="mt-s5">
        {query.error ? (
          <div className="mb-s4">
            <Banner
              tone="error"
              action={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void query.refetch()}
                >
                  {t('app.retry')}
                </Button>
              }
            >
              {t('tasks.error_loading')}
            </Banner>
          </div>
        ) : null}

        {query.isLoading ? (
          <Card>
            <div className="flex flex-col gap-s3" aria-busy>
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          </Card>
        ) : filtered.length === 0 ? (
          <Card>
            <EmptyState
              icon={<ListTodo className="h-7 w-7" />}
              title={t('tasks.empty_title')}
              description={
                search || assigneeId || status !== 'all' || deadline
                  ? t('tasks.empty_filtered_list')
                  : t('tasks.empty_description_clinic')
              }
              action={
                canCreate && !search ? (
                  <Button
                    type="button"
                    onClick={() => setCreating(true)}
                    disabled={!activeClinicId}
                  >
                    {t('tasks.create')}
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <Card className="p-0 nav:p-0">
            <div className="flex flex-wrap gap-x-s4 gap-y-s2 border-b border-ds-line px-s3 py-s3 nav:hidden">
              {sortableColumns.map((column) => (
                <span key={column.id}>{sortHeader(column.id, column.label)}</span>
              ))}
            </div>
            <ResponsiveTable
              rows={sorted}
              getRowKey={(row) => row.id}
              getRowLabel={(row) => row.title}
              onRowClick={(row) => setEditing(row)}
              columns={[
                {
                  id: 'done',
                  header: '',
                  cell: (row) => (
                    <button
                      type="button"
                      className="shrink-0 text-ds-teal disabled:opacity-50"
                      disabled={!activeClinicId || toggleDone.isPending}
                      aria-label={
                        row.status === 'done' ? t('tasks.mark_open') : t('tasks.mark_done')
                      }
                      onClick={() => {
                        if (!activeClinicId) return;
                        void toggleDone
                          .mutateAsync({
                            task_id: row.id,
                            clinic_id: activeClinicId,
                            done: row.status !== 'done',
                          })
                          .then(() =>
                            toast.success(
                              row.status === 'done'
                                ? t('tasks.toast_reopened')
                                : t('tasks.toast_completed'),
                            ),
                          )
                          .catch(() => toast.error(t('tasks.error_update')));
                      }}
                    >
                      <CheckCircle
                        className={cn(
                          'h-5 w-5',
                          row.status === 'done' ? 'text-ds-green-ink' : 'text-ds-line',
                        )}
                      />
                    </button>
                  ),
                },
                {
                  id: 'title',
                  header: sortHeader('title', t('tasks.col_title')),
                  sortDirection: ariaSort(sort, 'title'),
                  cell: (row) => (
                    <div>
                      <p
                        className={cn(
                          TYPE.body,
                          'text-ds-text',
                          row.status === 'done' && 'text-ds-muted line-through',
                        )}
                      >
                        {row.title}
                      </p>
                      {row.assignees.length > 0 ? (
                        <p className={cn(TYPE.small, 'text-ds-muted nav:hidden')}>
                          {row.assignees.map((assignee) => assignee.name).join(', ')}
                        </p>
                      ) : null}
                    </div>
                  ),
                },
                {
                  id: 'assignees',
                  header: sortHeader('assignees', t('tasks.col_assignees')),
                  sortDirection: ariaSort(sort, 'assignees'),
                  cell: (row) =>
                    row.assignees.length > 0
                      ? row.assignees.map((assignee) => assignee.name).join(', ')
                      : t('tasks.unassigned'),
                  hideOnMobile: true,
                },
                {
                  id: 'deadline',
                  header: sortHeader('deadline', t('tasks.col_deadline')),
                  sortDirection: ariaSort(sort, 'deadline'),
                  cell: (row) => {
                    const overdue = staffTaskIsOverdue(
                      row.deadline_date,
                      row.deadline_time,
                      row.status,
                    );
                    return (
                      <span className={overdue ? 'text-ds-danger' : undefined}>
                        {formatDeadline(row, dateLocale)}
                        {overdue ? ` · ${t('tasks.overdue')}` : ''}
                      </span>
                    );
                  },
                },
                {
                  id: 'priority',
                  header: sortHeader('priority', t('tasks.field_priority')),
                  sortDirection: ariaSort(sort, 'priority'),
                  cell: (row) => (
                    <StatusChip tone={priorityTone(row.priority)}>
                      {t(`tasks.priority.${row.priority}`)}
                    </StatusChip>
                  ),
                  hideOnMobile: true,
                },
                {
                  id: 'status',
                  header: sortHeader('status', t('tasks.field_status')),
                  sortDirection: ariaSort(sort, 'status'),
                  cell: (row) => (
                    <StatusChip tone={statusTone(row.status)}>
                      {t(`tasks.status.${row.status}`)}
                    </StatusChip>
                  ),
                },
              ]}
              mobileCard={(row) => (
                <div className="flex w-full items-start gap-s3 text-left">
                  <CheckCircle
                    className={cn(
                      'mt-0.5 h-5 w-5 shrink-0',
                      row.status === 'done' ? 'text-ds-green-ink' : 'text-ds-line',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        TYPE.body,
                        'text-ds-text',
                        row.status === 'done' && 'text-ds-muted line-through',
                      )}
                    >
                      {row.title}
                    </p>
                    <p
                      className={cn(
                        TYPE.small,
                        staffTaskIsOverdue(row.deadline_date, row.deadline_time, row.status)
                          ? 'text-ds-danger'
                          : 'text-ds-muted',
                      )}
                    >
                      {row.assignees.map((assignee) => assignee.name).join(', ') ||
                        t('tasks.unassigned')}
                      {' · '}
                      {formatDeadline(row, dateLocale)}
                    </p>
                  </div>
                  <StatusChip tone={statusTone(row.status)}>
                    {t(`tasks.status.${row.status}`)}
                  </StatusChip>
                </div>
              )}
            />
          </Card>
        )}
      </div>

      {creating && activeClinicId ? (
        <TaskFormSlideOver clinicId={activeClinicId} onClose={() => setCreating(false)} />
      ) : null}
      {editing && activeClinicId ? (
        <TaskFormSlideOver
          clinicId={activeClinicId}
          task={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </PageLayout>
  );
}

function ariaSort(
  sort: StaffTaskListSort,
  column: StaffTaskSortColumn,
): 'ascending' | 'descending' | 'none' {
  if (sort.column !== column) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}
